import { useState, useEffect, useCallback } from 'react';
import API_BASE from '../config';

// ─────────────────────────────────────────────────────────────
// Auth token — 4-way fallback pattern used across the portal
// ─────────────────────────────────────────────────────────────
function getToken() {
  try {
    return (
      localStorage.getItem('martech_token') ||
      sessionStorage.getItem('martech_token') ||
      JSON.parse(localStorage.getItem('martech_user') || '{}').token ||
      JSON.parse(sessionStorage.getItem('martech_user') || '{}').token ||
      null
    );
  } catch {
    return null;
  }
}

function authHeaders(extra = {}) {
  const token = getToken();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

async function apiGet(path) {
  const r = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.success === false) throw new Error(data.error || `Request failed (${r.status})`);
  return data;
}

async function apiSend(path, method, body) {
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.success === false) throw new Error(data.error || `Request failed (${r.status})`);
  return data;
}

async function downloadPdf(path, filename) {
  const r = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error || `Download failed (${r.status})`);
  }
  const blob = await r.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const TODAY = new Date();
const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDays = (isoStr, days) => {
  const d = new Date(isoStr);
  d.setDate(d.getDate() + days);
  return fmtISO(d);
};
const MAX_CUSTOM_RANGE_DAYS = 366;
const INIT_TODAY = fmtISO(TODAY);
const THEME_KEY = 'martech_invoice_theme';

const inr = (n, currency = 'INR') => {
  const symbol = currency === 'INR' ? '₹' : currency + ' ';
  return symbol + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SERVICE_LABELS = {
  google_ads: 'Google Ads',
  facebook_ads: 'Facebook Ads',
  instagram_ads: 'Instagram Ads',
  seo: 'SEO',
  consulting: 'Consulting',
};

const PERIOD_OPTIONS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'custom', label: 'Custom' },
];

function buildPeriodQuery({ period, customFrom, customTo }) {
  const params = new URLSearchParams({ period });
  if (period === 'custom') {
    params.set('from', customFrom);
    params.set('to', customTo);
  }
  return params.toString();
}

function validateCustomRange(from, to) {
  if (!from || !to) return 'Select both start and end dates.';
  if (new Date(from) > new Date(to)) return 'Start date must be before end date.';
  const days = Math.ceil((new Date(to) - new Date(from)) / 86400000);
  if (days > MAX_CUSTOM_RANGE_DAYS) return 'Custom range cannot exceed 1 year.';
  return null;
}

// ─────────────────────────────────────────────────────────────
// Theme + responsive CSS (light default, dark via data-theme, mobile via media queries)
// ─────────────────────────────────────────────────────────────
const INVOICE_CSS = `
.invoice-page {
  --bg-page: #f7f7f9;
  --bg-card: #ffffff;
  --text-primary: #18181b;
  --text-secondary: #52525b;
  --text-muted: #71717a;
  --border: #e4e4e7;
  --border-soft: #eeeef0;
  --input-bg: #ffffff;
  --input-border: #d4d4d8;
  --danger: #dc2626;
  --success: #16a34a;
  --info: #0284c7;
  --shadow: 0 4px 20px rgba(0,0,0,0.06);
  background: var(--bg-page);
  color: var(--text-primary);
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
}
.invoice-page-inner {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 24px 60px;
  box-sizing: border-box;
}
.invoice-page[data-theme="dark"] {
  --bg-page: #0a0a0a;
  --bg-card: #111111;
  --text-primary: #f4f4f5;
  --text-secondary: #a1a1aa;
  --text-muted: #71717a;
  --border: #222222;
  --border-soft: #1e1e1e;
  --input-bg: #1a1a1a;
  --input-border: #2a2a2a;
  --danger: #f87171;
  --success: #4ade80;
  --info: #38bdf8;
  --shadow: 0 8px 32px rgba(0,0,0,0.4);
}
.invoice-page * { box-sizing: border-box; }
.inv-toolbar { display: flex; justify-content: flex-end; margin-bottom: 16px; }
.inv-theme-btn {
  background: var(--input-bg); color: var(--text-secondary); border: 1px solid var(--input-border);
  border-radius: 10px; padding: 8px 14px; font-size: 12px; font-weight: 600; cursor: pointer;
}
.inv-theme-btn:hover { border-color: var(--text-secondary); }
.inv-section-title {
  font-size: 13px; font-weight: 700; color: var(--text-secondary); text-transform: uppercase;
  letter-spacing: 0.1em; margin: 0 0 16px;
}
.inv-card {
  background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px;
  overflow: hidden; box-shadow: var(--shadow); margin-bottom: 16px;
}
.inv-hdr { padding: 20px 28px; }
.inv-body { padding: 20px 28px; }
@media (max-width: 640px) {
  .invoice-page-inner { padding: 14px 12px 40px; }
  .inv-hdr { padding: 16px 18px; }
  .inv-body { padding: 16px 18px; }
}
.inv-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
.inv-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
@media (max-width: 640px) {
  .inv-grid-2, .inv-grid-3 { grid-template-columns: 1fr; gap: 14px; }
}
.inv-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 16px; }
.inv-table { width: 100%; border-collapse: collapse; min-width: 460px; }
.inv-table th {
  text-align: right; font-size: 11px; font-weight: 700; color: var(--text-secondary);
  text-transform: uppercase; letter-spacing: 0.08em; padding: 0 0 10px; white-space: nowrap;
}
.inv-table th:first-child, .inv-table td:first-child { text-align: left; }
.inv-table td {
  padding: 10px 0; font-size: 13px; color: var(--text-primary);
  border-bottom: 1px solid var(--border-soft); text-align: right; white-space: nowrap;
}
.inv-input, .inv-select, .inv-textarea {
  background: var(--input-bg); border: 1px solid var(--input-border); border-radius: 8px;
  color: var(--text-primary); padding: 8px 12px; font-size: 13px; width: 100%; outline: none;
}
.inv-textarea { resize: vertical; line-height: 1.6; }
.inv-label {
  font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase;
  letter-spacing: 0.08em; margin-bottom: 4px; display: block;
}
.inv-summary-box { width: 280px; max-width: 100%; margin-left: auto; margin-bottom: 16px; }
@media (max-width: 480px) { .inv-summary-box { width: 100%; } }
.inv-summary-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid var(--border-soft); font-size: 13px; color: var(--text-secondary); }
.inv-summary-total { display: flex; justify-content: space-between; padding: 10px 12px; margin-top: 6px; border-radius: 8px; font-weight: 800; font-size: 15px; }
.inv-btn-primary {
  display: inline-flex; align-items: center; gap: 7px; color: #fff; border: none; border-radius: 10px;
  padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer; letter-spacing: 0.01em;
}
.inv-btn-ghost {
  display: inline-flex; align-items: center; gap: 7px; background: var(--input-bg); color: var(--text-secondary);
  border: 1px solid var(--input-border); border-radius: 10px; padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.inv-btn-small { padding: 6px 12px; font-size: 12px; }
.inv-actions-row { display: flex; gap: 12px; justify-content: flex-end; flex-wrap: wrap; }
.inv-period-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 0; }
.inv-period-btn {
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
  border: 1px solid var(--input-border); background: var(--input-bg); color: var(--text-secondary);
}
.inv-period-btn.active { color: var(--accent); border-color: var(--accent); background: var(--accent-bg); }
.inv-msg-error { color: var(--danger); font-size: 13px; margin-bottom: 16px; }
.inv-msg-ok { color: var(--success); font-size: 13px; margin-bottom: 12px; }
.inv-msg-muted { color: var(--text-muted); font-size: 13px; margin-bottom: 16px; }
.inv-badge {
  padding: 3px 8px; border-radius: 999px; font-weight: 700; text-transform: uppercase; font-size: 10px;
}
.inv-divider { display: flex; align-items: center; gap: 14px; margin: 32px 0; }
.inv-divider-line { flex: 1; height: 1px; background: var(--border); }
.inv-divider-label { font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.14em; white-space: nowrap; }
@media print {
  .no-print { display: none !important; }
}
`;

// ─────────────────────────────────────────────────────────────
// UI helpers
// ─────────────────────────────────────────────────────────────
const btnPrimaryStyle = (c1 = '#7c3aed', c2 = '#6d28d9') => ({
  background: `linear-gradient(135deg, ${c1}, ${c2})`,
});

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

function PeriodFilter({ period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo, accent = '#7c3aed', accentBg = 'rgba(124,58,237,0.1)' }) {
  return (
    <div className="no-print" style={{ marginBottom: 20, '--accent': accent, '--accent-bg': accentBg }}>
      <div className="inv-period-row" style={{ marginBottom: period === 'custom' ? 12 : 0 }}>
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setPeriod(opt.value)}
            className={`inv-period-btn${period === opt.value ? ' active' : ''}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {period === 'custom' && (
        <div className="inv-grid-2" style={{ maxWidth: 400, marginTop: 12 }}>
          <div>
            <label className="inv-label">From</label>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="inv-input" max={customTo || INIT_TODAY} />
          </div>
          <div>
            <label className="inv-label">To</label>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="inv-input" max={INIT_TODAY} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Section 1 — usage-based platform invoice (view + PDF)
// ─────────────────────────────────────────────────────────────
function UsageInvoiceCard({ invoice }) {
  const isProvisional = invoice.status === 'provisional';
  return (
    <div className="inv-card">
      <div className="inv-hdr" style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 800, margin: 0 }}>{invoice.label}</h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, margin: '4px 0 0' }}>
              {invoice.periodStart} to {invoice.periodEnd} · {invoice.invoiceNumber}
            </p>
          </div>
          {isProvisional && (
            <span style={{ background: 'rgba(255,255,255,0.22)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Provisional
            </span>
          )}
        </div>
      </div>
      <div className="inv-body">
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>Channel</th><th>Unit Rate</th><th>Count</th><th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.messagingBreakdown || []).map((row) => (
                <tr key={row.channel}>
                  <td style={{ textTransform: 'capitalize' }}>{row.channel}</td>
                  <td>{inr(row.rate, invoice.currency)}/msg</td>
                  <td>{row.count.toLocaleString('en-IN')}</td>
                  <td>{inr(row.amount, invoice.currency)}</td>
                </tr>
              ))}
              {Number(invoice.retainerAmount) > 0 && (
                <tr>
                  <td>Platform Retainer</td>
                  <td>fixed</td>
                  <td>—</td>
                  <td>{inr(invoice.retainerAmount, invoice.currency)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(invoice.serviceBreakdown || []).length > 0 && (
          <>
            <div className="inv-label" style={{ marginBottom: 10 }}>Service Fees</div>
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Service</th><th>Basis</th><th>Spend</th><th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.serviceBreakdown.map((row) => {
                    const label = SERVICE_LABELS[row.service] || row.service;
                    const basis = row.pricingType === 'percentage' ? `${row.rateValue}% of spend` : 'Flat fee';
                    const spend = row.pricingType === 'percentage' ? inr(row.spend, invoice.currency) : '—';
                    return (
                      <tr key={row.service}>
                        <td>{label}</td>
                        <td>{basis}</td>
                        <td>{spend}</td>
                        <td>{inr(row.amount, invoice.currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="inv-summary-box">
          <div className="inv-summary-row">
            <span>Messaging + Retainer</span>
            <span>{inr(invoice.messagingTotal + Number(invoice.retainerAmount), invoice.currency)}</span>
          </div>
          {Number(invoice.serviceTotal) > 0 && (
            <div className="inv-summary-row">
              <span>Service fees</span>
              <span>{inr(invoice.serviceTotal, invoice.currency)}</span>
            </div>
          )}
          {Number(invoice.taxAmount) > 0 && (
            <div className="inv-summary-row">
              <span>Tax ({invoice.taxPercent}%)</span>
              <span>{inr(invoice.taxAmount, invoice.currency)}</span>
            </div>
          )}
          <div className="inv-summary-total" style={{ background: 'rgba(124,58,237,0.12)', color: '#a78bfa' }}>
            <span>Total</span>
            <span>{inr(invoice.totalAmount, invoice.currency)}</span>
          </div>
        </div>

        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => downloadPdf(`/api/v1/invoice/usage/${invoice.invoiceNumber}/pdf`, `${invoice.invoiceNumber}.pdf`)}
            className="inv-btn-primary"
            style={btnPrimaryStyle()}
          >
            <DownloadIcon /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function UsageInvoiceSection() {
  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState(addDays(INIT_TODAY, -30));
  const [customTo, setCustomTo] = useState(INIT_TODAY);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (period === 'custom') {
      const err = validateCustomRange(customFrom, customTo);
      if (err) { setError(err); setInvoices([]); return; }
    }
    setLoading(true);
    setError('');
    try {
      const qs = buildPeriodQuery({ period, customFrom, customTo });
      const data = await apiGet(`/api/v1/invoice/usage/summary?${qs}`);
      setInvoices(data.invoices || []);
    } catch (e) {
      setError(e.message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const combined = invoices.reduce((acc, inv) => ({
    messagingTotal: acc.messagingTotal + Number(inv.messagingTotal),
    retainerAmount: acc.retainerAmount + Number(inv.retainerAmount),
    serviceTotal: acc.serviceTotal + Number(inv.serviceTotal || 0),
    taxAmount: acc.taxAmount + Number(inv.taxAmount),
    totalAmount: acc.totalAmount + Number(inv.totalAmount),
  }), { messagingTotal: 0, retainerAmount: 0, serviceTotal: 0, taxAmount: 0, totalAmount: 0 });

  return (
    <div>
      <h1 className="inv-section-title">Your Invoice to Client</h1>

      <PeriodFilter period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} />

      {error && <div className="inv-msg-error">{error}</div>}
      {loading && <div className="inv-msg-muted">Loading usage…</div>}

      {!loading && !error && invoices.length === 0 && (
        <div className="inv-msg-muted">No usage data for this period.</div>
      )}

      {invoices.map((inv) => <UsageInvoiceCard key={inv.invoiceNumber} invoice={inv} />)}

      {period === 'last_6_months' && invoices.length > 1 && (
        <div className="inv-card">
          <div className="inv-body">
            <div className="inv-label" style={{ marginBottom: 10 }}>
              Combined — Last 6 Months (each month is still billed individually above)
            </div>
            <div className="inv-summary-row"><span>Messaging + Retainer</span><span>{inr(combined.messagingTotal + combined.retainerAmount)}</span></div>
            {combined.serviceTotal > 0 && (
              <div className="inv-summary-row"><span>Service fees</span><span>{inr(combined.serviceTotal)}</span></div>
            )}
            <div className="inv-summary-row"><span>Tax</span><span>{inr(combined.taxAmount)}</span></div>
            <div className="inv-summary-total" style={{ background: 'rgba(124,58,237,0.12)', color: '#a78bfa', marginTop: 8 }}>
              <span>Total (6 months)</span><span>{inr(combined.totalAmount)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Section 2 — client raises invoices for their own vendors/users
// ─────────────────────────────────────────────────────────────
function ClientInvoiceSection() {
  const emptyFrom = { name: '', address: '', gstin: '', email: '', phone: '' };
  const emptyTo = { name: '', address: '', gstin: '', email: '', phone: '' };

  const [from, setFrom] = useState(emptyFrom);
  const [to, setTo] = useState(emptyTo);
  const [invoiceDate, setInvoiceDate] = useState(INIT_TODAY);
  const [dueDate, setDueDate] = useState(addDays(INIT_TODAY, 30));
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [taxPercent, setTaxPercent] = useState(18);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState(addDays(INIT_TODAY, -30));
  const [customTo, setCustomTo] = useState(INIT_TODAY);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    apiGet('/api/v1/invoice/billing-profile')
      .then((data) => {
        if (data.profile) {
          setFrom({
            name: data.profile.company_name || '',
            address: data.profile.address || '',
            gstin: data.profile.gstin || '',
            email: data.profile.email || '',
            phone: data.profile.phone || '',
          });
        }
      })
      .catch(() => {});
  }, []);

  const loadHistory = useCallback(async () => {
    if (period === 'custom') {
      const err = validateCustomRange(customFrom, customTo);
      if (err) { setHistoryError(err); setHistory([]); return; }
    }
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const qs = buildPeriodQuery({ period, customFrom, customTo });
      const data = await apiGet(`/api/v1/invoice/client?${qs}`);
      setHistory(data.invoices || []);
    } catch (e) {
      setHistoryError(e.message);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const rows = items.map((it) => ({ ...it, amount: (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) }));
  const subtotal = rows.reduce((a, b) => a + b.amount, 0);
  const taxAmount = subtotal * (Number(taxPercent) / 100);
  const total = subtotal + taxAmount;

  const addItem = () => setItems((p) => [...p, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems((p) => (p.length === 1 ? p : p.filter((_, idx) => idx !== i)));
  const updateItem = (i, f, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [f]: v } : it)));

  const clearForm = () => {
    setTo(emptyTo);
    setInvoiceDate(INIT_TODAY);
    setDueDate(addDays(INIT_TODAY, 30));
    setItems([{ description: '', quantity: 1, unit_price: 0 }]);
    setNotes('');
    setSaveError(''); setSaveOk('');
  };

  const saveInvoice = async () => {
    setSaveError(''); setSaveOk('');
    if (!from.name.trim()) { setSaveError('Your company name (From) is required.'); return; }
    if (!to.name.trim()) { setSaveError('Customer/vendor name (To) is required.'); return; }
    const validItems = rows.filter((r) => r.description.trim());
    if (validItems.length === 0) { setSaveError('Add at least one line item.'); return; }

    setSaving(true);
    try {
      const payload = {
        from_name: from.name, from_address: from.address, from_gstin: from.gstin, from_email: from.email, from_phone: from.phone,
        bill_to_name: to.name, bill_to_address: to.address, bill_to_gstin: to.gstin, bill_to_email: to.email, bill_to_phone: to.phone,
        invoice_date: invoiceDate, due_date: dueDate,
        line_items: validItems.map((r) => ({ description: r.description, quantity: r.quantity, unit_price: r.unit_price })),
        tax_percent: taxPercent, currency: 'INR', notes,
      };
      const data = await apiSend('/api/v1/invoice/client', 'POST', payload);
      setSaveOk(`Saved as ${data.invoice.invoice_number}.`);
      clearForm();
      loadHistory();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const voidInvoice = async (id) => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    try {
      await apiSend(`/api/v1/invoice/client/${id}`, 'DELETE', {});
      loadHistory();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div>
      <h1 className="inv-section-title">Generate Invoice for Your Customer / Vendor</h1>

      <div className="inv-card">
        <div className="inv-hdr" style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)' }}>
          <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 800, margin: 0 }}>New Invoice</h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, margin: '4px 0 0' }}>Raised by you, for your customer or vendor</p>
        </div>
        <div className="inv-body">
          <div className="inv-grid-2" style={{ marginBottom: 24 }}>
            <div>
              <span className="inv-label" style={{ fontSize: 12, color: '#0ea5e9' }}>From (Your Company)</span>
              {[
                { key: 'name', ph: 'Company Name' }, { key: 'address', ph: 'Address' },
                { key: 'gstin', ph: 'GSTIN' }, { key: 'email', ph: 'Email' }, { key: 'phone', ph: 'Phone' },
              ].map((f) => (
                <div key={f.key} style={{ marginTop: 10 }}>
                  <label className="inv-label">{f.ph}</label>
                  <input value={from[f.key]} onChange={(e) => setFrom((p) => ({ ...p, [f.key]: e.target.value }))} className="inv-input" placeholder={f.ph} />
                </div>
              ))}
            </div>
            <div>
              <span className="inv-label" style={{ fontSize: 12, color: '#0ea5e9' }}>To (Customer / Vendor)</span>
              {[
                { key: 'name', ph: 'Name' }, { key: 'address', ph: 'Address' },
                { key: 'gstin', ph: 'GSTIN (Optional)' }, { key: 'email', ph: 'Email' }, { key: 'phone', ph: 'Phone' },
              ].map((f) => (
                <div key={f.key} style={{ marginTop: 10 }}>
                  <label className="inv-label">{f.ph}</label>
                  <input value={to[f.key]} onChange={(e) => setTo((p) => ({ ...p, [f.key]: e.target.value }))} className="inv-input" placeholder={f.ph} />
                </div>
              ))}
            </div>
          </div>

          <div className="inv-grid-3" style={{ marginBottom: 24 }}>
            <div><label className="inv-label">Invoice Date</label><input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="inv-input" /></div>
            <div><label className="inv-label">Due Date</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="inv-input" /></div>
            <div><label className="inv-label">Tax %</label><input type="number" min="0" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} className="inv-input" /></div>
          </div>

          <span className="inv-label" style={{ fontSize: 12 }}>Line Items</span>
          <div className="inv-table-wrap" style={{ marginTop: 8 }}>
            <table className="inv-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th style={{ width: 72 }}>Qty</th>
                  <th style={{ width: 120 }}>Unit Price</th>
                  <th style={{ width: 110 }}>Amount</th>
                  <th style={{ width: 32 }} className="no-print" />
                </tr>
              </thead>
              <tbody>
                {rows.map((item, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: 'left' }}><input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="inv-input" placeholder="Description" /></td>
                    <td><input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} className="inv-input" style={{ textAlign: 'right' }} /></td>
                    <td><input type="number" min="0" value={item.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} className="inv-input" style={{ textAlign: 'right' }} /></td>
                    <td>{inr(item.amount)}</td>
                    <td className="no-print">
                      <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="no-print inv-btn-ghost inv-btn-small" onClick={addItem} style={{ marginBottom: 24 }}>+ Add Item</button>

          <div className="inv-summary-box">
            <div className="inv-summary-row"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="inv-summary-row"><span>Tax ({taxPercent}%)</span><span>{inr(taxAmount)}</span></div>
            <div className="inv-summary-total" style={{ background: 'rgba(14,165,233,0.12)', color: '#38bdf8' }}>
              <span>Total</span><span>{inr(total)}</span>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="inv-label">Notes (Optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Payment terms, bank details, etc." className="inv-textarea inv-input" />
          </div>

          {saveError && <div className="inv-msg-error" style={{ marginBottom: 12 }}>{saveError}</div>}
          {saveOk && <div className="inv-msg-ok">{saveOk}</div>}

          <div className="inv-actions-row">
            <button onClick={clearForm} className="inv-btn-ghost">Clear</button>
            <button onClick={saveInvoice} disabled={saving} className="inv-btn-primary" style={{ ...btnPrimaryStyle('#0ea5e9', '#0284c7'), opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save & Generate'}
            </button>
          </div>
        </div>
      </div>

      <div>
        <span className="inv-label" style={{ fontSize: 12, marginBottom: 12, display: 'block' }}>Invoice History</span>
        <PeriodFilter period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} accent="#0ea5e9" accentBg="rgba(14,165,233,0.1)" />

        {historyError && <div className="inv-msg-error" style={{ marginBottom: 12 }}>{historyError}</div>}
        {historyLoading && <div className="inv-msg-muted" style={{ marginBottom: 12 }}>Loading…</div>}
        {!historyLoading && !historyError && history.length === 0 && (
          <div className="inv-msg-muted">No invoices in this period.</div>
        )}

        {history.length > 0 && (
          <div className="inv-card" style={{ marginBottom: 0 }}>
            <div className="inv-table-wrap">
              <table className="inv-table" style={{ minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Invoice #</th>
                    <th style={{ padding: '12px 16px' }}>Bill To</th>
                    <th style={{ padding: '12px 16px' }}>Date</th>
                    <th style={{ padding: '12px 16px' }}>Total</th>
                    <th style={{ padding: '12px 16px' }}>Status</th>
                    <th style={{ padding: '12px 16px' }} />
                  </tr>
                </thead>
                <tbody>
                  {history.map((inv) => (
                    <tr key={inv.id}>
                      <td style={{ textAlign: 'left', padding: '10px 16px' }}>{inv.invoice_number}</td>
                      <td style={{ padding: '10px 16px' }}>{inv.bill_to_name}</td>
                      <td style={{ padding: '10px 16px' }}>{inv.invoice_date?.slice(0, 10)}</td>
                      <td style={{ padding: '10px 16px' }}>{inr(inv.total_amount, inv.currency)}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span
                          className="inv-badge"
                          style={{
                            color: inv.status === 'void' ? 'var(--danger)' : inv.status === 'paid' ? 'var(--success)' : 'var(--info)',
                            background: inv.status === 'void' ? 'rgba(248,113,113,0.12)' : inv.status === 'paid' ? 'rgba(74,222,128,0.12)' : 'rgba(56,189,248,0.12)',
                          }}
                        >{inv.status}</span>
                      </td>
                      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                        <button onClick={() => downloadPdf(`/api/v1/invoice/client/${inv.id}/pdf`, `${inv.invoice_number}.pdf`)} className="inv-btn-ghost inv-btn-small" style={{ marginRight: 8 }}>PDF</button>
                        {inv.status !== 'void' && <button onClick={() => voidInvoice(inv.id)} className="inv-btn-ghost inv-btn-small" style={{ color: 'var(--danger)' }}>Void</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Page — wraps both sections in a themeable, responsive container
// ─────────────────────────────────────────────────────────────
export default function Invoice() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || 'light'; } catch { return 'light'; }
  });

  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, theme); } catch {}
  }, [theme]);

  return (
    <div className="invoice-page" data-theme={theme}>
      <style>{INVOICE_CSS}</style>
      <div className="invoice-page-inner">
        <div className="inv-toolbar no-print">
          <button
            className="inv-theme-btn"
            onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            aria-label="Toggle light/dark theme"
          >
            {theme === 'light' ? <><MoonIcon /> Dark mode</> : <><SunIcon /> Light mode</>}
          </button>
        </div>

        <UsageInvoiceSection />

        <div className="inv-divider">
          <div className="inv-divider-line" />
          <span className="inv-divider-label">Client Invoice Generator</span>
          <div className="inv-divider-line" />
        </div>

        <ClientInvoiceSection />
      </div>
    </div>
  );
}
